<template>
  <div class="detail-root">
    <!-- 顶栏：返回 + 账号 -->
    <div class="top-bar">
      <span class="back" @click="goBack">↩</span>
      <div class="account-badge">
        <span class="avatar">{{ userName.charAt(0) }}</span>
        <div class="name-line">
          <span class="name">{{ userName }}</span>
          <span class="nick" v-if="nickName">{{ nickName }}</span>
        </div>
      </div>
      <div class="top-actions">
        <span class="range-btn" :class="{ on: range === 'today' }" @click="setRange('today')">今</span>
        <span class="range-btn" :class="{ on: range === 'yesterday' }" @click="setRange('yesterday')">昨</span>
        <span class="range-btn" :class="{ on: range === '2d' }" @click="setRange('2d')">2d</span>
        <span class="range-btn" :class="{ on: range === '3d' }" @click="setRange('3d')">3d</span>
        <span class="range-btn" :class="{ on: range === '7d' }" @click="setRange('7d')">7d</span>
      </div>
    </div>

    <!-- 实时状态 -->
    <div class="status-strip">
      <div class="ss-item">
        <span class="ss-label">状态</span>
        <span class="ss-val" :class="online ? 'on' : 'off'">{{ online ? '直播中' : '离线' }}</span>
      </div>
      <div class="ss-item">
        <span class="ss-label">所在房间</span>
        <span class="ss-val">{{ resolveRoomName(room) || '未进房' }}</span>
      </div>
      <div class="ss-item">
        <span class="ss-label">上线时间</span>
        <span class="ss-val">{{ onlineTime ? fmtTime(onlineTime) : '—' }}</span>
      </div>
      <div class="ss-item">
        <span class="ss-label">最后离线</span>
        <span class="ss-val">{{ lastOfflineTime ? fmtTime(lastOfflineTime) : '—' }}</span>
      </div>
    </div>

    <!-- 直播统计 -->
    <div class="stats-bar" v-if="debugMode">
      <div class="stat-card">
        <span class="sc-val">{{ totalCount }}</span>
        <span class="sc-label">直播场次</span>
      </div>
      <div class="stat-card" :class="netWinCardClass">
        <span class="sc-val" :class="netWinClass">{{ totalNetWinStr }}</span>
        <span class="sc-label">直播收入</span>
      </div>
    </div>

    <!-- 内容区：撑满剩余空间，内部滚动 -->
    <div class="content-wrap">
      <div class="scroll-area">
        <div v-if="loading" class="loading">正在拉取直播数据...</div>
        <div v-else-if="records.length === 0" class="empty">暂无直播记录</div>

        <div class="record" v-for="r in records" :key="r.order_number || r.id" :class="[recordBoxClass(r), { unfinished: !r.is_finish }]">
          <!-- 头部：直播间信息 -->
          <div class="rec-top">
            <div class="rec-top-row1">
              <span class="live-dot" v-if="!r.is_finish"></span>
              <span class="rec-desk">{{ roomDisplayName(r) }}</span>
              <span class="rec-status" :class="{ done: r.is_finish }">
                {{ r.is_finish ? '已结束' : '直播中' }}
              </span>
              <span class="rec-result-chip" v-if="debugMode && betLabel(r)" :class="betColorClass(r)">
                {{ betLabel(r) }}<i v-if="r.odds"> · {{ r.odds }}</i>
              </span>
            </div>
          </div>

          <!-- PK 对战区 - 真实模式：传统扑克牌 -->
          <div class="pk-real-showdown" v-if="debugMode && (r.xian_cards || r.zhuang_cards)">
            <!-- 信息行 -->
            <div class="showdown-info">
              <span class="rec-time">{{ formatTime(r.bet_time) }}</span>
              <span class="rec-round">第{{ r.main_ju_number }}回合</span>
              <span class="rec-settle-time" v-if="r.settlement_time">结束于 {{ formatTime(r.settlement_time) }}</span>
            </div>
            <!-- 对决行 -->
            <div class="showdown-row">
              <!-- 我方卡牌 -->
              <div class="playing-card" v-for="(c, ci) in parseCards(r.xian_cards)" :key="'x'+ci" :class="c.suit">
                <span class="pc-rank">{{ realRankDisplay(c.count) }}</span>
                <span class="pc-suit">{{ realSuitSymbol(c.suit) }}</span>
              </div>
              <div v-if="!parseCards(r.xian_cards).length" class="playing-card empty">
                <span class="pc-rank">-</span>
              </div>
              <!-- VS 比分 -->
              <div class="showdown-vs">
                <span class="vs-score-xian" v-if="r.xian_dian != null">{{ r.xian_dian }}</span>
                <span class="vs-word">vs</span>
                <span class="vs-score-zhuang" v-if="r.zhuang_dian != null">{{ r.zhuang_dian }}</span>
              </div>
              <!-- 对方卡牌 -->
              <div class="playing-card" v-for="(c, ci) in parseCards(r.zhuang_cards)" :key="'z'+ci" :class="c.suit">
                <span class="pc-rank">{{ realRankDisplay(c.count) }}</span>
                <span class="pc-suit">{{ realSuitSymbol(c.suit) }}</span>
              </div>
              <div v-if="!parseCards(r.zhuang_cards).length" class="playing-card empty">
                <span class="pc-rank">-</span>
              </div>
            </div>
          </div>

          <!-- PK 对战区 - 礼物模式：图标徽章 -->
          <div class="pk-zone" v-else-if="!debugMode && (r.xian_cards || r.zhuang_cards)">
            <div class="pk-row">
              <span class="pk-tag blue">我方</span>
              <span class="pk-cards">
                <span class="chip" :class="c.suit" v-for="(c, ci) in parseCards(r.xian_cards)" :key="'x'+ci">
                  {{ c.symbol }}×{{ c.count }}
                </span>
                <span v-if="!parseCards(r.xian_cards).length" class="chip empty">-</span>
              </span>
              <span class="pk-pts" v-if="r.xian_dian != null">{{ r.xian_dian }}<i>分</i></span>
            </div>
            <div class="pk-divider"><span>VS</span></div>
            <div class="pk-row">
              <span class="pk-tag red">对方</span>
              <span class="pk-cards">
                <span class="chip" :class="c.suit" v-for="(c, ci) in parseCards(r.zhuang_cards)" :key="'z'+ci">
                  {{ c.symbol }}×{{ c.count }}
                </span>
                <span v-if="!parseCards(r.zhuang_cards).length" class="chip empty">-</span>
              </span>
              <span class="pk-pts" v-if="r.zhuang_dian != null">{{ r.zhuang_dian }}<i>分</i></span>
            </div>
          </div>

          <!-- 收益明细 -->
          <div class="rec-amounts">
            <div class="am">
              <span class="am-icon">🎁</span>
              <span class="am-val gold">{{ Tools.formatMoney(r.total_betting_amount) }}</span>
            </div>
            <div class="am">
              <span class="am-icon">💰</span>
              <span class="am-val" :class="winColor(r.real_win_total_amount)">{{ Tools.formatMoney(r.real_win_total_amount) }}</span>
            </div>
            <div class="am">
              <span class="am-icon">💳</span>
              <span class="am-val dim">{{ Tools.formatMoney(r.front_balance) }} → {{ Tools.formatMoney(r.after_balance) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 底部分页：始终在底部 -->
      <div class="pager-bar">
        <span class="pg-btn" :class="{ off: currentPage <= 1 }" @click="goPage(currentPage - 1)">‹</span>
        <span class="pg-num" v-if="totalPages > 0">{{ currentPage }} / {{ totalPages }} 页</span>
        <span class="pg-num" v-else>暂无数据</span>
        <span class="pg-btn" :class="{ off: currentPage >= totalPages }" @click="goPage(currentPage + 1)">›</span>
      </div>
    </div>

    <!-- 悬浮刷新 -->
    <span class="float-refresh" @click="loadData()">↻</span>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, inject, type Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Tools from '@/ts/Tools';
import { GAME_TYPE_MAP } from '@/ts/roomName';
import { resolveRoomName, setRoomNameMap } from '@/ts/roomNameStore';
import RoomVO from '@/ts/VO/RoomVO';
import SignalRClient from '@/ts/SignalRClient';

const route = useRoute();
const router = useRouter();
const debugMode = inject<Ref<boolean>>('debugMode', ref(false));
const userName = ref(route.params.userName as string || '');
const nickName = ref('');

// 返回时回到主播列表页（首页的“主播列表”标签）
function goBack() {
  router.push({ name: 'home', query: { tab: 'recharge' } });
}

// 实时状态（来自列表接口 / GetAccountBets / 服务端推送）
// 进入详情时先用列表已知的实时状态做种子，避免接口滞后导致误判“离线”
const online = ref(route.query.online === '1');
const onlineTime = ref((route.query.ot as string) || '');
const lastOfflineTime = ref((route.query.lo as string) || '');
const room = ref((route.query.room as string) || '');

const range = ref('today');
const loading = ref(false);
const records = ref<any[]>([]);
const currentPage = ref(1);
const totalPages = ref(0);
const totalCount = ref(0);
const totalNetWin = ref(0);
const pageSize = 20;

// 推送已处理过的订单号 / 该订单已计入的收入金额（用于去重，重新拉页时清空）
const pushedOrders = new Set<string>();
const pushedWin = new Map<string, number>();

// ===== 伪装/真实卡牌切换（由父组件统一 debugMode 控制） =====
const REAL_SUIT: Record<string, string> = { spade: '♠', heart: '♥', club: '♣', diamond: '♦' };
const REAL_RANK: Record<number, string> = { 1: 'A', 11: 'J', 12: 'Q', 13: 'K' };
function realSuitSymbol(suit: string) { return REAL_SUIT[suit] || ''; }
function realRankDisplay(n: number) { return REAL_RANK[n] || String(n); }

const totalNetWinStr = computed(() => {
  const v = totalNetWin.value;
  return Tools.formatMoney(v, { showSign: true });
});
const netWinClass = computed(() => totalNetWin.value > 0 ? 'positive' : totalNetWin.value < 0 ? 'negative' : '');
const netWinCardClass = computed(() => totalNetWin.value > 0 ? 'card-win' : totalNetWin.value < 0 ? 'card-lose' : '');
function setRange(r: string) {
  range.value = r;
  currentPage.value = 1;
  loadData();
}

function goPage(p: number) {
  if (p < 1 || p > totalPages.value) return;
  currentPage.value = p;
  loadData();
}

async function loadData() {
  loading.value = true;
  try {
    const now = new Date();
    let start: string, end: string = toDateStr(now);
    if (range.value === 'today') start = end;
    else if (range.value === 'yesterday') { start = end = toDateStr(new Date(now.getTime() - 86400000)); }
    else if (range.value === '2d') start = toDateStr(new Date(now.getTime() - 86400000));
    else if (range.value === '3d') start = toDateStr(new Date(now.getTime() - 2 * 86400000));
    else if (range.value === '7d') start = toDateStr(new Date(now.getTime() - 6 * 86400000));
    else start = toDateStr(new Date(now.getTime() - 29 * 86400000));

    const resp = await Tools.FetchGetAsync<any>('Game/GetAccountBets', {
      userName: userName.value,
      startDate: start,
      endDate: end,
      page: currentPage.value,
      pageSize: pageSize
    });
    if (resp.CheckIsOk() && resp.Data) {
      records.value = resp.Data.data || [];
      // 整页重拉：服务端数据才是准的，清空推送去重账本
      pushedOrders.clear();
      pushedWin.clear();
      totalPages.value = resp.Data.pagecount || 0;
      totalCount.value = resp.Data.allcount || 0;
      // 后端聚合的统计，不再前端拉全量数据计算
      if (resp.Data.stats) {
        totalNetWin.value = resp.Data.stats.total_win || 0;
      }
      if (records.value.length > 0 && records.value[0].nickName) {
        nickName.value = records.value[0].nickName;
      }
      // 实时状态字段（服务端实时计算）
      // online 以“已确认为在线”为准：列表种子或服务端推送若已判在线，接口的滞后 false 不将其降级
      online.value = online.value || !!resp.Data.online;
      onlineTime.value = resp.Data.onlineTime || onlineTime.value;
      lastOfflineTime.value = resp.Data.lastOfflineTime || lastOfflineTime.value;
      room.value = resp.Data.room || room.value;
    }
  } finally {
    loading.value = false;
  }
}

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ===== 直播间互动玩法映射（见 @/ts/roomName）=====
function roomDisplayName(r: any): string {
  const zhuo = r.game_zhuo_name || '';
  const gn = r.game_name || '';
  const gc = zhuo; //r.game_code || '';

  // if (gn && zhuo) return `${gn} ${zhuo}`;
  // if (gn) return `${gn} ${gc}`;
  // if (zhuo) return zhuo;
  // 兜底：用前端映射
  const type = typeof r.game_type === 'string' ? parseInt(r.game_type) : r.game_type;
  const name = GAME_TYPE_MAP[type];
  if (name && gc) return `${name} ${gc}`;
  return name || gc || '';
}

// ===== 直播互动结果判断（仅 debug/真实模式显示） =====

function betLabel(r: any): string {
  if (!r.is_finish) return '';
  return r.bet_direction || '';
}

function betColorClass(r: any): string {
  const win = Number(r.real_win_total_amount) || 0;
  if (win > 0) return 'result-win';
  if (win < 0) return 'result-lose';
  return '';
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function winColor(val: number): string {
  if (val > 0) return 'positive';
  if (val < 0) return 'negative';
  return 'zero';
}

function recordBoxClass(r: any): string {
  const win = Number(r.real_win_total_amount) || 0;
  if (win > 0) return 'record-win';
  if (win < 0) return 'record-lose';
  return '';
}

// ===== 卡牌解析 =====
const SUIT_MAP: Record<string, { symbol: string; cls: string }> = {
  S: { symbol: '🖤', cls: 'spade' },    // 黑桃 — 黑色心/桃
  H: { symbol: '❤️', cls: 'heart' },    // 红桃 — 红色心
  C: { symbol: '🌸', cls: 'club' },     // 梅花 — 花
  D: { symbol: '🟦', cls: 'diamond' },  // 方片 — 方块
};
const RANK_VALUE: Record<string, number> = { '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, '11': 11, '12': 12, '13': 13 };

function parseCards(raw: string | null): { count: number; symbol: string; suit: string }[] {
  if (!raw || raw === '-' || raw === '[]') return [];
  try {
    const arr: string[] = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((c: string) => typeof c === 'string' && c.includes('.'))
      .map((c: string) => {
        const [suit, rank] = c.split('.');
        const info = SUIT_MAP[suit] || { symbol: suit, cls: '' };
        return { count: RANK_VALUE[rank] || 0, symbol: info.symbol, suit: info.cls };
      });
  } catch {
    return [];
  }
}

// 下注/结算推送：服务端已带完整记录，前端直接插入/更新列表，不重新拉取整页
function applyPushedRecord(rec: any) {
  // 只处理当前查看的账号；列表按 bet_time 倒序，新记录只可能落在第 1 页
  if (!rec || !rec.order_number) return;
  if (rec.userName !== userName.value) return;
  if (currentPage.value !== 1) return;
  // “昨天”区间不含今天，推送的新记录不属于该区间，直接丢弃
  if (range.value === 'yesterday') return;

  const no = rec.order_number;
  const idx = records.value.findIndex((r: any) => r && r.order_number === no);
  if (idx >= 0) {
    // 已存在（例如刚打开页面已经刷到这条）→ 原地更新，不新增条目，避免重复
    records.value[idx] = rec;
  } else if (!pushedOrders.has(no)) {
    // 新记录插到最前；超过每页固定条数则移除末尾多余的（推送 100 条也只显示一页）
    records.value = [rec, ...records.value].slice(0, pageSize);
    totalCount.value += 1;                                   // 直播场次
    totalPages.value = Math.max(totalPages.value, Math.ceil(totalCount.value / pageSize));
  }
  pushedOrders.add(no);

  // 直播收入：仅已结算才计入；同一单只累加差额，避免下注→结算两次推送重复计
  if (rec.is_finish === 1) {
    const win = Number(rec.real_win_total_amount) || 0;
    totalNetWin.value += win - (pushedWin.get(no) || 0);
    pushedWin.set(no, win);
  }
  if (rec.nickName) nickName.value = rec.nickName;
}
const onAccountDataPush = (data: any) => applyPushedRecord(data?.record);

onMounted(() => {
  loadData();
  // 兜底：深链/刷新进入时首页未加载房间列表，主动拉一次填充 RoomNumber->RoomName 映射
  SignalRClient.Instance.InvokeAsync<{ [key: string]: RoomVO }>("GetRoomList")
    .then((resp) => {
      const list: RoomVO[] = [];
      for (const key in resp) {
        const room = resp[key];
        Object.setPrototypeOf(room, RoomVO.prototype);
        list.push(room);
      }
      setRoomNameMap(list);
    })
    .catch(() => {});
  // 服务端推送的账号状态实时更新（在线/房间/时间）
  SignalRClient.Instance.AddEventListener("onUserStatusUpdate", (data: any) => {
    if (data?.userName !== userName.value) return;
    online.value = !!data.online;
    onlineTime.value = data.onlineTime || '';
    lastOfflineTime.value = data.lastOfflineTime || '';
    room.value = data.room || '';
  });
  // 下注/结算推送：服务端带完整记录，前端直接插入/更新（去重 + 限制每页条数）
  SignalRClient.Instance.AddEventListener("onBet", onAccountDataPush);
  SignalRClient.Instance.AddEventListener("onSettle", onAccountDataPush);
});

onUnmounted(() => {
  SignalRClient.Instance.RemoveEventListener("onUserStatusUpdate");
  SignalRClient.Instance.RemoveEventListener("onBet");
  SignalRClient.Instance.RemoveEventListener("onSettle");
});

function fmtTime(v: any): string {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
</script>

<style scoped lang="scss">
.detail-root {
  position: relative;
  background: #2a2a2a;
  border-radius: 12px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

// ===== 顶栏 =====
.top-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.back {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  font-size: 16px;
  color: #f59e0b;
  font-weight: 700;
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.2);
  transition: all .15s;
  user-select: none;

  &:hover {
    background: rgba(245, 158, 11, 0.2);
    border-color: rgba(245, 158, 11, 0.35);
  }
}

.account-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: #1a1a1a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 800;
  flex-shrink: 0;
}

.name-line {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.name {
  font-size: 15px;
  font-weight: 700;
  color: #eee;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nick {
  font-size: 11px;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.top-actions {
  display: flex;
  gap: 2px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 8px;
  padding: 3px;
  flex-shrink: 0;
}

.range-btn {
  font-size: 11px;
  color: #777;
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  transition: all .15s;

  &:hover { color: #ccc; }

  &.on {
    background: rgba(255, 255, 255, 0.08);
    color: #eee;
    font-weight: 600;
  }
}

// ===== 实时状态条 =====
.status-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.02);
  flex-shrink: 0;

  .ss-item {
    display: flex;
    align-items: baseline;
    gap: 6px;
    min-width: 0;
  }

  .ss-label {
    font-size: 10px;
    color: #777;
    flex-shrink: 0;
  }

  .ss-val {
    font-size: 12px;
    font-weight: 600;
    color: #e0e0e0;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;

    &.on { color: #4ade80; }
    &.off { color: #888; }
  }
}

// ===== 统计条 =====
.stats-bar {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.stat-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  padding: 10px 6px;
  border: 1px solid transparent;
  transition: all 0.3s;

  &.card-win {
    background: linear-gradient(135deg, rgba(74, 222, 128, 0.12), rgba(74, 222, 128, 0.04));
    border-color: rgba(74, 222, 128, 0.2);
    .sc-label { color: #4ade80; }
  }

  &.card-lose {
    background: linear-gradient(135deg, rgba(248, 113, 113, 0.12), rgba(248, 113, 113, 0.04));
    border-color: rgba(248, 113, 113, 0.2);
    .sc-label { color: #f87171; }
  }
}

.sc-val {
  font-size: 20px;
  font-weight: 800;
  color: #eee;
  line-height: 1;
  font-variant-numeric: tabular-nums;

  &.positive { color: #4ade80; }
  &.negative { color: #f87171; }
}

.sc-label {
  font-size: 10px;
  color: #666;
  font-weight: 500;
}

// ===== 内容区（列表 + 分页）=====
.content-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
}

.scroll-area {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 10px;
  min-height: 0;
  min-width: 0;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.08);
    border-radius: 2px;
  }
}

.loading, .empty {
  text-align: center;
  padding: 80px 0;
  color: #555;
  font-size: 13px;
}

// ===== 记录卡片 =====
.record {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.02));
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 10px;
  transition: all .2s;
  overflow: hidden;
  min-width: 0;

  &:hover { background: linear-gradient(135deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.03)); }

  &.unfinished {
    border-color: rgba(245, 158, 11, 0.2);
  }

  &.record-win {
    background: linear-gradient(135deg, rgba(74, 222, 128, 0.08), rgba(74, 222, 128, 0.02));
    border-color: rgba(74, 222, 128, 0.15);
    &:hover { background: linear-gradient(135deg, rgba(74, 222, 128, 0.12), rgba(74, 222, 128, 0.04)); }
  }

  &.record-lose {
    background: linear-gradient(135deg, rgba(248, 113, 113, 0.08), rgba(248, 113, 113, 0.02));
    border-color: rgba(248, 113, 113, 0.15);
    &:hover { background: linear-gradient(135deg, rgba(248, 113, 113, 0.12), rgba(248, 113, 113, 0.04)); }
  }
}

// ===== 卡片头部 =====
.rec-top {
  display: flex;
  margin-bottom: 10px;
}

.rec-top-row1 {
  display: flex;
  align-items: center;
  gap: 8px;
}

// 脉冲红点 - 直播中
.live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ef4444;
  flex-shrink: 0;
  box-shadow: 0 0 6px rgba(239, 68, 68, 0.6);
  animation: pulse-dot 1.5s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.3); }
}

.rec-desk {
  font-size: 13px;
  color: #e0e0e0;
  font-weight: 700;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rec-status {
  font-size: 10px;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.12);
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
  flex-shrink: 0;
  font-weight: 600;

  &.done {
    color: #888;
    background: rgba(255, 255, 255, 0.06);
  }
}

.rec-time {
  font-size: 10px;
  color: #666;
  white-space: nowrap;
}

.rec-round {
  font-size: 10px;
  color: #60a5fa;
  font-weight: 600;
  white-space: nowrap;
}

.rec-settle-time {
  font-size: 9px;
  color: #555;
  white-space: nowrap;
}

.rec-result-chip {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 4px;
  margin-left: 6px;
  white-space: nowrap;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.05);
  color: #888;
  border: 1px solid rgba(255, 255, 255, 0.06);

  i {
    font-style: normal;
    color: #a78bfa;
    font-weight: 500;
    margin-left: 2px;
  }

  &.result-win {
    color: #4ade80;
    background: rgba(74, 222, 128, 0.08);
    border-color: rgba(74, 222, 128, 0.12);
  }

  &.result-lose {
    color: #f87171;
    background: rgba(248, 113, 113, 0.08);
    border-color: rgba(248, 113, 113, 0.12);
  }
}

// ===== PK 对战区 =====
.pk-zone {
  position: relative;
  padding: 12px 16px;
  margin-bottom: 8px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.015);
  border: 1px solid rgba(255, 255, 255, 0.03);
}

.pk-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
  flex-wrap: wrap;
}

.pk-tag {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
  flex-shrink: 0;
  min-width: 36px;
  text-align: center;

  &.blue { color: #60a5fa; background: rgba(59, 130, 246, 0.12); }
  &.red  { color: #f87171; background: rgba(239, 68, 68, 0.12); }
}

.pk-cards {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  min-width: 0;
}

.pk-pts {
  margin-left: auto;
  font-size: 16px;
  font-weight: 900;
  color: #e0e0e0;
  letter-spacing: 1px;
  flex-shrink: 0;

  i {
    font-style: normal;
    font-size: 10px;
    color: #666;
    font-weight: 500;
    margin-left: 2px;
  }
}

.pk-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  margin: 3px 0 3px 36px;
  position: relative;

  &::before, &::after {
    content: '';
    position: absolute;
    top: 50%;
    height: 1px;
    width: calc(50% - 24px);
    background: rgba(255,255,255,0.05);
  }
  &::before { left: 0; }
  &::after  { right: 0; }

  span {
    font-size: 10px;
    font-weight: 800;
    color: #444;
    letter-spacing: 1px;
    padding: 0 8px;
    z-index: 1;
  }
}


// ===== 礼物标记 =====
.chip {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 4px 10px;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  line-height: 1.2;
  letter-spacing: 0.3px;
  border: 1px solid transparent;

  &.spade   { color: #e2b87a; border-color: rgba(226,184,122,0.18); background: rgba(226,184,122,0.08); }
  &.club    { color: #5eeaba; border-color: rgba(94,234,186,0.15); background: rgba(94,234,186,0.06); }
  &.heart   { color: #fca5af; border-color: rgba(252,165,175,0.18); background: rgba(252,165,175,0.08); }
  &.diamond { color: #67e8f9; border-color: rgba(103,232,249,0.15); background: rgba(103,232,249,0.06); }

  &.empty {
    color: #444;
    font-weight: 400;
    background: transparent;
    border-color: transparent;
  }
}

// ===== 真实扑克牌对决布局 =====
.pk-real-showdown {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 6px;
  border-radius: 8px;
  background: rgba(255,255,255,0.02);
  border: 1px solid rgba(255,255,255,0.04);
}

.showdown-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.showdown-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-wrap: wrap;
}

// 对决中心
.showdown-vs {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0 8px;
  flex-shrink: 0;

  .vs-score-xian {
    font-size: 22px;
    font-weight: 900;
    color: #60a5fa;
    font-variant-numeric: tabular-nums;
    line-height: 1;
  }
  .vs-score-zhuang {
    font-size: 22px;
    font-weight: 900;
    color: #f87171;
    font-variant-numeric: tabular-nums;
    line-height: 1;
  }
  .vs-word {
    font-size: 10px;
    font-weight: 700;
    color: rgba(255,255,255,0.12);
    letter-spacing: 0.5px;
    text-transform: lowercase;
    flex-shrink: 0;
  }
}

// ===== 传统扑克牌 =====
.playing-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 42px;
  border-radius: 4px;
  background: linear-gradient(145deg, #f3efe6, #e8e1cd);
  border: 1px solid rgba(180,170,140,0.5);
  box-shadow:
    0 1px 3px rgba(0,0,0,0.3),
    inset 0 0 0 1px rgba(255,255,255,0.25);
  flex-shrink: 0;

  .pc-rank {
    font-size: 14px;
    font-weight: 900;
    line-height: 1;
  }

  .pc-suit {
    font-size: 14px;
    line-height: 1;
    margin-top: -1px;
  }

  &.spade, &.club {
    .pc-rank, .pc-suit { color: #1a1a2e; }
  }
  &.heart, &.diamond {
    .pc-rank, .pc-suit { color: #c53030; }
  }

  &.empty {
    background: rgba(255,255,255,0.02);
    border: 1px dashed rgba(255,255,255,0.06);
    box-shadow: none;
    .pc-rank { color: #444; font-weight: 400; }
  }
}

// ===== 收益明细 =====
.rec-amounts {
  display: flex;
  justify-content: space-around;
  min-width: 0;
}

.am {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.am-icon {
  font-size: 12px;
  flex-shrink: 0;
}

.am-val {
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;

  &.gold    { color: #ffd700; }
  &.positive { color: #4ade80; }
  &.negative { color: #f87171; }
  &.zero    { color: #888; }
  &.dim     { color: #999; font-size: 10px; font-weight: 400; }
}

// ===== 底部分页：固定 =====
.pager-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 10px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.pg-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 16px;
  color: #aaa;
  cursor: pointer;
  user-select: none;
  transition: all .15s;

  &:hover { background: rgba(255,255,255,.06); color: #fff; }

  &.off {
    opacity: 0.25;
    pointer-events: none;
  }
}

.pg-num {
  font-size: 12px;
  color: #777;
  font-variant-numeric: tabular-nums;
  min-width: 60px;
  text-align: center;
}

// ===== 悬浮刷新 =====
.float-refresh {
  position: absolute;
  right: 16px;
  bottom: 16px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: #60a5fa;
  background: rgba(96, 165, 250, 0.15);
  border: 1px solid rgba(96, 165, 250, 0.25);
  cursor: pointer;
  user-select: none;
  transition: all .15s;
  z-index: 10;

  &:hover {
    background: rgba(96, 165, 250, 0.25);
    border-color: rgba(96, 165, 250, 0.4);
    box-shadow: 0 0 12px rgba(96, 165, 250, 0.2);
  }

  &:active {
    transform: scale(0.9) rotate(180deg);
  }
}
</style>
